/**
 * Sentry initialization for the React frontend.
 *
 * Reads `VITE_SENTRY_DSN` (public) and `VITE_SENTRY_ENVIRONMENT` (optional,
 * defaults to import.meta.env.MODE). If no DSN is set, Sentry is a no-op —
 * safe to leave the package installed in environments where Sentry isn't
 * configured (preview deploys, CI, etc.).
 *
 * Free-tier-friendly defaults:
 *   - tracesSampleRate: 0       → no performance traces (paid feature)
 *   - replaysSessionSampleRate: 0 → no session replay (paid)
 *   - replaysOnErrorSampleRate: 0
 *   - profilesSampleRate: 0
 *
 * Only error reporting is enabled, which is unlimited on the free tier
 * within the 5,000-event/month cap.
 */
import * as Sentry from "@sentry/react";

// Public DSN — safe to commit. Sentry's threat model assumes DSNs are visible
// in browser bundles (they identify the project, not authenticate writes).
// Build-time env vars (VITE_SENTRY_DSN) override this fallback when present.
const FALLBACK_DSN =
  "https://029f07c09fd3db1d949c9a034216467c@o4511305043673088.ingest.de.sentry.io/4511305050882128";
const FALLBACK_ENVIRONMENT = "production";

export function initSentry() {
  const dsn =
    (import.meta.env.VITE_SENTRY_DSN as string | undefined) || FALLBACK_DSN;
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.info("[Sentry] VITE_SENTRY_DSN not set; error reporting disabled.");
    }
    return;
  }

  Sentry.init({
    dsn,
    environment:
      (import.meta.env.VITE_SENTRY_ENVIRONMENT as string) ||
      (import.meta.env.PROD ? FALLBACK_ENVIRONMENT : import.meta.env.MODE),
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,

    // Errors only — keep within the free tier.
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    profilesSampleRate: 0,

    // Filter out noise that doesn't reflect real bugs.
    ignoreErrors: [
      // Browser extensions
      /^Non-Error promise rejection captured/,
      // Network blips outside our control
      /Failed to fetch/,
      /NetworkError when attempting to fetch resource/,
      /Load failed/,
      // ResizeObserver loop benign noise
      /ResizeObserver loop limit exceeded/,
      /ResizeObserver loop completed with undelivered notifications/,
      // Common safari quirks
      /AbortError: The user aborted a request/,
    ],

    // Don't report errors from third-party scripts that aren't ours.
    denyUrls: [
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
      /^safari-extension:\/\//,
    ],

    beforeSend(event) {
      // Drop events from local dev unless explicitly opted in
      if (import.meta.env.DEV && !import.meta.env.VITE_SENTRY_FORCE_DEV) {
        return null;
      }
      return event;
    },
  });
}

export { Sentry };
